import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, FolderOpen, AlertCircle, ShieldOff } from "lucide-react";
import { collectionsApi, type CreateCollectionDto } from "@/lib/api/collections";
import { COLLECTIONS_QUERY_KEY } from "@/hooks/useCollections";
import { cn } from "@/lib/utils";

const CollectionFormPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Form validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Form state
  const [formData, setFormData] = useState<CreateCollectionDto>({
    name: "",
    code: "",
    description: "",
    isActive: true,
  });

  const { isTenantAdmin } = useAccessControl();
  const isEditing = !!id;

  useEffect(() => {
    if (isTenantAdmin && isEditing && id) {
      loadCollection(id);
    }
  }, [isTenantAdmin, isEditing, id]);

  // Track unsaved changes
  useEffect(() => {
    if (!loading) {
      setHasUnsavedChanges(true);
    }
  }, [formData]);

  const loadCollection = async (collectionId: string) => {
    try {
      setLoading(true);
      const collection = await collectionsApi.getById(collectionId);
      setFormData({
        name: collection.name,
        code: collection.code || "",
        description: collection.description || "",
        isActive: collection.isActive,
      });
      setHasUnsavedChanges(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to load collection.");
      navigate("/setup/collections");
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "name":
        if (!value || !value.trim()) return "Name is required";
        if (value.length < 2) return "Name must be at least 2 characters";
        if (value.length > 100) return "Name must be less than 100 characters";
        return "";
      case "code":
        if (value && value.length > 20) return "Code must be less than 20 characters";
        return "";
      default:
        return "";
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    newErrors.name = validateField("name", formData.name);
    newErrors.code = validateField("code", formData.code);

    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, v]) => v !== "")
    );

    setErrors(filteredErrors);
    setTouched(new Set(["name", "code"]));

    return Object.keys(filteredErrors).length === 0;
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => new Set(prev).add(field));
    const value = formData[field as keyof typeof formData];
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before saving.");
      return;
    }

    try {
      setSaving(true);
      const submitData = {
        ...formData,
        code: formData.code || undefined,
        description: formData.description || undefined,
      };

      if (isEditing && id) {
        await collectionsApi.update(id, submitData);
        toast.success(`"${formData.name}" has been updated successfully.`);
      } else {
        await collectionsApi.create(submitData);
        toast.success(`"${formData.name}" has been created successfully.`);
      }

      queryClient.invalidateQueries(COLLECTIONS_QUERY_KEY);
      setHasUnsavedChanges(false);
      navigate("/setup/collections");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation("/setup/collections");
      setShowDiscardDialog(true);
    } else {
      navigate("/setup/collections");
    }
  };

  const handleDiscardConfirm = () => {
    setHasUnsavedChanges(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const getFieldStatus = (field: string) => {
    if (!touched.has(field)) return "default";
    if (errors[field]) return "error";
    return "success";
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage collections.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (loading && isEditing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <LoadingSpinner size="xl" label="Loading collection..." />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer>
        <div className="mb-6">
          <PageHeader
            title={isEditing ? "Edit Collection" : "Add Collection"}
            description={
              isEditing
                ? "Update the details for this collection"
                : "Create a new grouping for related library materials"
            }
            breadcrumbItems={[
              { label: "Setup", href: "/setup" },
              { label: "Collections", href: "/setup/collections" },
              { label: isEditing ? "Edit" : "New" },
            ]}
            showSearch={false}
          />
        </div>

        <form onSubmit={handleSubmit}>
          {/* Collection Details */}
          <Card className="p-6 mb-6 bg-white shadow-sm">
            <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
              <div className="p-2 rounded-lg bg-primary/10">
                <FolderOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Collection Details</h3>
                <p className="text-sm text-gray-500">Basic information about this collection</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label htmlFor="name" className="flex items-center gap-1 mb-2">
                  Collection Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onBlur={() => handleFieldBlur("name")}
                  placeholder="e.g., Local History Archive, Rare Books"
                  className={cn(
                    getFieldStatus("name") === "error" && "border-red-500 focus:border-red-500",
                    getFieldStatus("name") === "success" &&
                      "border-green-500 focus:border-green-500"
                  )}
                />
                {touched.has("name") && errors.name && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.name}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-500">
                  A clear name that describes what materials belong in this collection
                </p>
              </div>

              <div>
                <Label htmlFor="code" className="flex items-center gap-1 mb-2">
                  Short Code
                  <span className="text-xs font-normal text-gray-400">(optional)</span>
                </Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  onBlur={() => handleFieldBlur("code")}
                  placeholder="e.g., LHA, RARE, KIDS"
                  maxLength={20}
                  className={cn(
                    getFieldStatus("code") === "error" && "border-red-500 focus:border-red-500"
                  )}
                />
                {touched.has("code") && errors.code && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.code}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-500">
                  A brief abbreviation used for quick reference (auto-capitalized)
                </p>
              </div>

              <div>
                <Label htmlFor="description" className="flex items-center gap-1 mb-2">
                  Description
                  <span className="text-xs font-normal text-gray-400">(optional)</span>
                </Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Briefly describe what materials belong in this collection"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Additional details to help staff understand what goes in this collection
                </p>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                <Checkbox
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked === true })
                  }
                />
                <div className="flex-1">
                  <Label htmlFor="isActive" className="font-medium cursor-pointer">
                    Active
                  </Label>
                  <p className="text-xs text-gray-500">
                    Only active collections appear in selection lists
                  </p>
                </div>
                <span
                  className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium",
                    formData.isActive ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                  )}
                >
                  {formData.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </Card>

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-current rounded-full animate-spin border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? "Save Changes" : "Create Collection"}
                </>
              )}
            </Button>
          </div>
        </form>
      </PageContainer>

      {/* Discard Changes Dialog */}
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        title="Discard unsaved changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={handleDiscardConfirm}
        variant="warning"
      />
    </AppLayout>
  );
};

export default CollectionFormPage;
